import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, Button, Select, useConnectionRetry, FensterKnopf, useGeteilterWert, useEinsatz } from '@pilot-hub/ui'
import { api, userApi } from '@pilot-hub/api-client'
import { Video, VideoOff, Camera, Info, Lock, Map as MapIcon, Plane, WifiOff } from 'lucide-react'
import type { LivebildInfo, LivebildKamera } from '../components/Livebild'

// Live-Video für die Führung: Bild der per DJI Pilot 2 (Cloud API) verbundenen Drohne, analog zum
// Co-Pilot-Modus im Pilot-Modul (apps/pilot/src/pages/CoPilotLiveVideoPage.tsx) – aber ohne Linsenwahl:
// Die Linse/Kamera legt der Pilot fest, die Führung sieht sie nur an (Umschalten würde auch das Bild auf
// der Fernsteuerung ändern).

interface LiveDrohne {
  sn: string
  in_der_luft: boolean
  alter_s: number
  drohne: string
  kennzeichen: string | null
  fernsteuerung: string | null
  livebild?: LivebildInfo
}

const LINSEN: Record<string, string> = { wide: 'Weitwinkel', zoom: 'Zoom', ir: 'Wärmebild', normal: 'Standard' }
const linsenName = (l: string | null | undefined) => (l ? LINSEN[l] ?? l : '–')

/** camera_index = {Typ}-{Untertyp}-{Position}; Position 7 = FPV-Kamera (DJI Payload-Index). */
const kameraName = (k: LivebildKamera) =>
  !k.drohne ? 'Kamera der Fernsteuerung' : k.camera_index.endsWith('-7') ? 'FPV-Kamera' : 'Gimbal-Kamera'

interface EingeteilterPilot { id: number; name: string; rolle: 'Pilot' | 'Co-Pilot' }

/** Eingeteilte Piloten/Co-Piloten des gewählten Einsatzes (Kräfte-Management) für den Leerzustand */
function useEingeteiltePiloten(einsatzId: number | null): EingeteilterPilot[] {
  const [liste, setListe] = useState<EingeteilterPilot[]>([])
  useEffect(() => {
    if (einsatzId == null) { setListe([]); return }
    let abgebrochen = false
    ;(async () => {
      try {
        const [km, u] = await Promise.all([api.get(`/kraefte-management/${einsatzId}`), userApi.list({ size: 500 })])
        const namen = new Map<number, string>()
        for (const x of (u.data?.items ?? []) as any[]) namen.set(x.id, `${x.vorname ?? ''} ${x.nachname ?? ''}`.trim() || x.benutzername)
        const bloecke = (km.data?.data?.bloecke ?? km.data?.bloecke ?? []) as any[]
        const aus: EingeteilterPilot[] = []
        for (const b of bloecke) for (const r of (b.besatzung ?? [])) {
          if (r.nutzerId && (r.funktion === 'PILOT' || r.funktion === 'CO_PILOT') && !aus.some(a => a.id === r.nutzerId)) {
            aus.push({ id: r.nutzerId, name: namen.get(r.nutzerId) ?? `Nutzer ${r.nutzerId}`, rolle: r.funktion === 'PILOT' ? 'Pilot' : 'Co-Pilot' })
          }
        }
        if (!abgebrochen) setListe(aus)
      } catch { if (!abgebrochen) setListe([]) }
    })()
    return () => { abgebrochen = true }
  }, [einsatzId])
  return liste
}

export function LiveVideoPage() {
  const { einsatzId } = useEinsatz()
  const piloten = useEingeteiltePiloten(einsatzId)
  const [drohnen, setDrohnen] = useState<LiveDrohne[]>([])
  // Multimonitor: gewählte Drohne gilt in allen Fenstern des Moduls
  const [auswahl, setAuswahl] = useGeteilterWert<string>('fuehrung', 'livebild-drohne', '')
  const [geladen, setGeladen] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [aktionLaeuft, setAktionLaeuft] = useState(false)
  const [aktionFehler, setAktionFehler] = useState<string | null>(null)

  const laden = () => api.get('/dji/live')
    .then(r => { setDrohnen(r.data as LiveDrohne[]); setFehler(null) })
    .catch(() => setFehler('Live-Daten konnten nicht geladen werden.'))
    .finally(() => setGeladen(true))

  useEffect(() => {
    laden()
    const t = setInterval(() => { if (document.visibilityState !== 'hidden') laden() }, 2000)
    return () => clearInterval(t)
  }, [])
  useConnectionRetry(() => { void laden() })

  // Drohne in der Luft bevorzugen; Auswahl bleibt, solange die Drohne gemeldet wird
  useEffect(() => {
    if (auswahl && drohnen.some(d => d.sn === auswahl)) return
    const erste = drohnen.find(d => d.in_der_luft) ?? drohnen[0]
    setAuswahl(erste?.sn ?? '', false)
  }, [drohnen, auswahl])

  const drohne = drohnen.find(d => d.sn === auswahl) ?? null
  const lb = drohne?.livebild
  const kameras = lb?.kameras ?? []
  const aktiveKamera = kameras.find(k => k.aktiv) ?? null
  const streamAktiv = !!(lb?.laeuft || lb?.dji_meldet_stream)

  async function steuern(pfad: string) {
    if (!drohne) return
    setAktionLaeuft(true); setAktionFehler(null)
    try {
      await api.post(`/dji/live/${encodeURIComponent(drohne.sn)}/livebild/${pfad}`)
      laden()
    } catch (e: any) {
      setAktionFehler(e?.response?.data?.detail || 'Aktion fehlgeschlagen')
    } finally { setAktionLaeuft(false) }
  }

  if (!geladen) return <div className="p-6 text-sm text-dark-text-muted">Lade Live-Daten …</div>

  if (!drohne) {
    return (
      <div className="space-y-4" data-testid="livevideo-leer">
        <Card className="p-5">
          <div className="flex items-start gap-3 text-dark-text-muted">
            <VideoOff className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-dark-text">{fehler ?? 'Keine Drohne verbunden'}</p>
              <p className="mt-1 text-sm">Das Live-Video erscheint hier, sobald eine Fernsteuerung angemeldet ist. Drohne anmelden: DJI Pilot 2 öffnen, Cloud-Dienst wählen und die Anmeldeseite des Pilot-Hub laden.</p>
              <Link to="/taktische-karte" className="mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-dark-border px-3 text-sm font-semibold text-dark-text hover:bg-dark-bg-tertiary">
                <MapIcon className="h-4 w-4 text-primary-400" /> Zur taktischen Karte
              </Link>
            </div>
          </div>
        </Card>
        <div>
          <div className="mb-2 text-sm font-semibold text-dark-text">Piloten im Einsatz</div>
          {piloten.length === 0 ? (
            <Card className="p-4 text-xs text-dark-text-subtle">Keine Piloten eingeteilt (oder kein Einsatz gewählt). Die Einteilung erfolgt im Kräfte-Management.</Card>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {piloten.map(pl => (
                <Card key={pl.id} className="p-4" data-testid="pilot-kachel">
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-sm font-semibold text-dark-text">{pl.name}</span>
                    <span className="shrink-0 text-xs text-dark-text-subtle">{pl.rolle}</span>
                  </div>
                  <div className="mt-3 flex aspect-video items-center justify-center rounded-lg bg-black/60 text-dark-text-subtle">
                    <WifiOff className="h-7 w-7" />
                  </div>
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-dark-text-muted"><Plane className="h-3.5 w-3.5" />offline – keine Drohne verbunden</p>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {fehler && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{fehler}</div>}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Bild der Fernsteuerung */}
        <Card className="overflow-hidden p-0 lg:col-span-2">
          <div className="flex items-center justify-between border-b border-dark-border px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-semibold text-dark-text">
              <Video className="h-4 w-4 text-primary-400" /> Bild der Fernsteuerung
            </span>
            <span className="flex items-center gap-2">
              {streamAktiv && (
                <span className="rounded-full bg-primary-500/15 px-2.5 py-1 text-xs font-semibold text-primary-200">
                  {linsenName(lb?.linse ?? aktiveKamera?.linse)}
                </span>
              )}
              <FensterKnopf ansicht="livebild" />
            </span>
          </div>
          <div className="relative aspect-video w-full bg-black">
            {lb?.url ? (
              <iframe src={lb.url} title={`Live-Video ${drohne.kennzeichen || drohne.drohne}`}
                className="absolute inset-0 h-full w-full" allow="autoplay; fullscreen" allowFullScreen />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center text-sm text-dark-text-muted">
                <VideoOff className="h-8 w-8" />
                {lb?.dji_meldet_stream ? 'Die Drohne sendet – das Bild wird gleich angezeigt …' : 'Kein Live-Video. Über „Live-Video anfordern" starten.'}
              </div>
            )}
          </div>
        </Card>

        {/* Steuerung */}
        <div className="space-y-4">
          <Card className="p-4">
            <div className="mb-3 text-sm font-semibold text-dark-text">Drohne</div>
            {drohnen.length > 1 ? (
              <Select value={auswahl} onChange={e => setAuswahl(e.target.value)}
                options={drohnen.map(d => ({ value: d.sn, label: `${d.kennzeichen || d.drohne}${d.in_der_luft ? ' · in der Luft' : ''}` }))} />
            ) : (
              <div className="text-sm text-dark-text">{drohne.kennzeichen || drohne.drohne}</div>
            )}
            <div className="mt-1 text-xs text-dark-text-subtle">
              {drohne.in_der_luft ? 'in der Luft' : 'am Boden'} · Fernsteuerung {drohne.fernsteuerung ?? '–'}
            </div>

            <div className="mt-4 flex gap-2">
              {!streamAktiv ? (
                <Button className="min-h-[44px] flex-1" disabled={aktionLaeuft || !lb?.anforderbar} onClick={() => steuern('start')}>
                  <Video className="mr-2 h-4 w-4" /> Live-Video anfordern
                </Button>
              ) : (
                <Button variant="secondary" className="min-h-[44px] flex-1" disabled={aktionLaeuft} onClick={() => steuern('stop')}>
                  <VideoOff className="mr-2 h-4 w-4" /> Beenden
                </Button>
              )}
            </div>
          </Card>

          <Card className="p-4">
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-dark-text">
              Linse <Lock className="h-3.5 w-3.5 text-dark-text-subtle" aria-label="nicht änderbar" />
            </div>
            <div className="text-base font-semibold text-dark-text">{streamAktiv ? linsenName(lb?.linse ?? aktiveKamera?.linse) : '–'}</div>
            <p className="mt-1 text-xs text-dark-text-subtle">Die Kamera und Linse wählt der Pilot bzw. Co-Pilot. Ein Wechsel ist hier nicht möglich, weil er auch das Bild auf der Fernsteuerung ändern würde.</p>
          </Card>

          {(aktionFehler || lb?.meldung) && (
            <div className={`rounded-lg border px-3 py-2 text-xs ${aktionFehler ? 'border-red-500/30 bg-red-500/10 text-red-200' : 'border-dark-border bg-dark-bg-tertiary text-dark-text-muted'}`}>
              {aktionFehler || lb?.meldung}
            </div>
          )}
        </div>
      </div>

      {/* Kameras laut Fernsteuerung */}
      <div>
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-dark-text">
          <Camera className="h-4 w-4 text-primary-400" /> Kameras
        </div>
        {lb?.gleichzeitig_max === 1 && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-dark-border bg-dark-bg-tertiary/60 px-3 py-2 text-xs text-dark-text-muted">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Die Fernsteuerung überträgt nur ein Video gleichzeitig. Andere Kameras und Linsen kann nur der Pilot bzw. Co-Pilot wählen.
          </div>
        )}
        {kameras.length === 0 ? (
          <Card className="p-4 text-xs text-dark-text-subtle">
            Die Fernsteuerung meldet noch keine Kameras – Drohne eingeschaltet und Anmeldeseite in DJI Pilot 2 geöffnet?
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {kameras.map(k => (
              <Card key={k.video_id} className={`p-4 ${k.aktiv ? 'ring-2 ring-primary-500/50' : ''}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-dark-text">{kameraName(k)}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${k.aktiv ? 'bg-emerald-500/15 text-emerald-300' : 'bg-dark-bg-tertiary text-dark-text-subtle'}`}>
                    {k.aktiv ? 'wird übertragen' : 'nicht aktiv'}
                  </span>
                </div>
                <div className="mt-1 text-xs text-dark-text-subtle">Index {k.camera_index}</div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(k.linsen.length ? k.linsen : [k.linse ?? 'normal']).map(l => (
                    <span key={l} className={`rounded-md border px-2 py-0.5 text-xs ${k.aktiv && (lb?.linse ?? k.linse) === l
                      ? 'border-primary-500 text-primary-200' : 'border-dark-border text-dark-text-muted'}`}>
                      {linsenName(l)}
                    </span>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
